import Translator from "@/utils/common/Translator.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import DateFieldUtils from "@/utils/common/DateFieldUtils.ts";
import NamaIconUtils from "@/utils/common/NamaIconUtils.ts";
import {NamaIconProps} from "@/utils/presenters/interfaces.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import {computed, ComputedRef, WritableComputedRef} from "vue";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import ListPresenter from "@/utils/common/ListPresenter.ts";
import SearcherViewPresenter from "@/utils/common/SearcherViewPresenter.ts";
import router from "@/router";
import QuickFilterURLSerializer from "@/utils/urlUtils/QuickFilterURLSerializer.ts";
import ListViewQuickFilterButtonVal = namaTypes.ListViewQuickFilterButtonVal;
import ListViewQuickFilterGroupVal = namaTypes.ListViewQuickFilterGroupVal;
import GenericValue = namaTypes.GenericValue;
import ListViewQuickFilterSelectedValues = namaTypes.ListViewQuickFilterSelectedValues;

export default class QuickFilterUtils {


  constructor(public presenter: ListPresenter | SearcherViewPresenter) {
  }

  getQuickFilterColumnValueButtonText(button: ListViewQuickFilterButtonVal): string {
    if (ObjectChecker.isNotEmptyOrNull(button.title))
      return Translator.translateNaMaText(button.title);
    if (!button.columnValues?.length)
      return "";
    let tempFieldId = button.columnValues[0].fieldId;
    let displayedText = "";
    for (let i = 0; i < button.columnValues.length; i++) {
      const columnValue = button.columnValues[i];
      const columnMetaData = button.columnMetadata?.[i];
      if (columnValue.fieldId !== tempFieldId)
        displayedText += " - ";
      displayedText += this.getQuickFilterDisplayedTextForType(columnMetaData, columnValue);
      tempFieldId = columnValue.fieldId;
    }
    return displayedText;
  }

  getQuickFilterDisplayedTextForType(metaData: namaTypes.BasicFieldMetaData | undefined, columnValue: namaTypes.GenericValue): string {
    if (columnValue.empty)
      return Translator.translateId("Empty");
    switch (metaData?.fieldType) {
      case FieldType.Enum:
        return this.renderAsEnumText(metaData, columnValue);
      case FieldType.Boolean:
        return this.renderAsBooleanText(columnValue);
      case FieldType.Date:
        return this.renderAsDateText(columnValue);
      case FieldType.DateAndTime:
        return this.renderAsDateAndTimeText(columnValue);
      case FieldType.Time:
        return this.renderAsTimeText(columnValue);
      case FieldType.Decimal:
        return this.renderAsDecimalText(columnValue);
      case FieldType.Integer:
      case FieldType.Long:
        return this.renderAsNumericText(columnValue);
      case FieldType.Reference:
      case FieldType.Genericreference:
        return this.renderAsReferenceText(columnValue);
      default:
        return this.renderAsText(columnValue);
    }
  }

  getQuickFilterPercentage(quickFilterGroup: ListViewQuickFilterGroupVal, button: ListViewQuickFilterButtonVal): string {
    if (!button?.columnMetadata?.length || !quickFilterGroup.buttons?.length)
      return "0";

    const relatedColumns = quickFilterGroup.buttons.filter(btn => !btn.criteriaId);
    const totalCount = relatedColumns.reduce((sum, btn) => sum + (btn.count ?? 0), 0);

    if (totalCount === 0)
      return "0";
    const percentage = ((button.count ?? 0) / totalCount) * 100;
    return percentage.toFixed(0);
  }

  getQuickFilterButtonBG(button: ListViewQuickFilterButtonVal): ComputedRef<string> {
    return computed(() => {
      const colorCode = NamaIconUtils.toColorCode(this.quickFilterButtonToNamaIconProps(button));
      if (colorCode)
        return colorCode;
      else if (useNamaFlagsStore().darkMode)
        return "var(--nama-primary-dark)"
      else
        return "var(--nama-primary-light)"
    })
  }

  isQuickFilterButtonSelected(selectedButton: ListViewQuickFilterButtonVal): ComputedRef<boolean> {
    return computed<boolean>(() => {
      const groups = this.presenter.selectedQuickFilterGroups.value?.map(g => g.selectedValues).flat();
      return groups.includes(selectedButton);
    })
  }

  quickFilterButtonToNamaIconProps(button: ListViewQuickFilterButtonVal): NamaIconProps | undefined {
    let namaIconProps: NamaIconProps = {defaultIcon: "nm-filter"};
    if (!this.isQuickFilterColumnValueButton(button))
      return {defaultIcon: "nm-criteria-filter"};
    const columnMetaData = button.columnMetadata?.[0];
    switch (columnMetaData?.fieldType) {
      case FieldType.Enum:
        namaIconProps = {
          enumTypeName: columnMetaData.enumTypeName,
          allowedValue: button.columnValues?.[0]?.textData,
        };
        break;
      case FieldType.Boolean:
        namaIconProps = {
          constant: button.columnValues?.[0].booleanData?.toString().toUpperCase()
        }
        break;
      case FieldType.Reference:
      case FieldType.Genericreference:
        namaIconProps = {
          constant: button.columnValues?.[0]?.entityReferenceData?.entityType,
        };
        break;
      default:
        namaIconProps = {
          defaultIcon: "nm-filter"
        };
        break;
    }
    namaIconProps.defaultIcon = "nm-filter";
    return namaIconProps;
  }

  isQuickFilterColumnValueButton(button: ListViewQuickFilterButtonVal): boolean {
    return !!button.columnValues?.length;
  }

  private wrapInStrong(content: string): string {
    return `<strong>${content}</strong>`;
  }

  private renderEmptyValue(): string {
    return this.wrapInStrong("-");
  }

  private renderTextValue(value?: string): string {
    if (ObjectChecker.isEmptyOrNull(value))
      return this.renderEmptyValue();
    else
      return this.wrapInStrong(value);
  }

  private renderNumericValue(value: any, options?: Intl.NumberFormatOptions): string {
    if (ObjectChecker.isEmptyOrNull(value))
      return this.renderEmptyValue();
    const formatted = Number(value).toLocaleString(undefined, options);
    return this.wrapInStrong(formatted);
  }

  renderAsEnumText(metaData: namaTypes.BasicFieldMetaData, columnValue: namaTypes.GenericValue): string {
    const translatedValue = Translator.translateId(metaData.enumTypeName + "." + columnValue.textData);
    return this.wrapInStrong(translatedValue);
  }

  renderAsDateText(columnValue: namaTypes.GenericValue): string {
    const dateString = DateFieldUtils.dateToString(columnValue.dateData);
    return this.wrapInStrong(dateString);
  }

  renderAsDateAndTimeText(columnValue: namaTypes.GenericValue): string {
    const dateTimeString = DateFieldUtils.dateAndTimeToString(columnValue.dateData);
    return this.wrapInStrong(dateTimeString);
  }

  renderAsReferenceText(columnValue: GenericValue): string {
    const translatedRef = Translator.translateRef(columnValue.entityReferenceData);
    return this.wrapInStrong(translatedRef);
  }

  renderAsBooleanText(columnValue: namaTypes.GenericValue): string {
    const booleanText = columnValue.booleanData ? Translator.translateId('yes') : Translator.translateId('no');
    return Translator.translateId(columnValue.fieldId) + ` ( ${this.wrapInStrong(booleanText)} )`;
  }

  renderAsText(columnValue: namaTypes.GenericValue): string {
    return this.renderTextValue(Translator.translateId(columnValue.textData));
  }

  renderAsNumericText(columnValue: namaTypes.GenericValue): string {
    const numValue = columnValue.longData || columnValue.textData;
    return this.renderNumericValue(numValue);
  }

  renderAsDecimalText(columnValue: namaTypes.GenericValue): string {
    const numValue = columnValue.bigDecimalData || columnValue.textData;
    return this.renderNumericValue(numValue, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    });
  }

  renderAsTimeText(columnValue: namaTypes.GenericValue): string {
    const timeString = DateFieldUtils.timeToString(columnValue.dateData?.dateValue);
    return this.wrapInStrong(timeString);
  }

  getQuickFilterTitle(group: ListViewQuickFilterGroupVal): string {
    if (!group.buttons?.length)
      return "";
    const groupTitle = Translator.translateNaMaText(group.title);
    if (ObjectChecker.isNotEmptyOrNull(groupTitle))
      return groupTitle;
    const firstButton = group.buttons[0];
    let title = "";
    if (ObjectChecker.isNotEmptyOrNull(firstButton.title))
      title = Translator.translateNaMaText(firstButton.title);
    else if (ObjectChecker.isNotEmptyOrNull(firstButton.columnMetadata)) {
      for (let metaData of firstButton.columnMetadata) {
        if (ObjectChecker.isNotEmptyOrNull(title))
          title += " - ";
        title += Translator.translateId(metaData?.fieldId);
      }
    }
    return title;
  }

  toggleSelectedQuickFilterButtonForDropdown(group: ListViewQuickFilterGroupVal, quickFilterButtons?: ListViewQuickFilterButtonVal[]) {
    if (ObjectChecker.isNullOrUndefined(quickFilterButtons))
      quickFilterButtons = [];
    for (const button of group.buttons ?? []) {
      let isSelected = this.isQuickFilterButtonSelected(button).value;
      if (isSelected && !quickFilterButtons.includes(button))
        this.unSelectQuickFilter(button);
      else if (!isSelected && quickFilterButtons.includes(button))
        this.selectQuickFilter(button);
    }
  }

  toggleSelectedQuickFilterButton(quickFilterButton: ListViewQuickFilterButtonVal) {
    if (!this.isQuickFilterButtonSelected(quickFilterButton).value)
      this.selectQuickFilter(quickFilterButton);
    else
      this.unSelectQuickFilter(quickFilterButton);
  }

  private selectQuickFilter(quickFilterButton: namaTypes.ListViewQuickFilterButtonVal) {
    const selectedQuickFilterGroups = this.presenter.selectedQuickFilterGroups.value;
    for (const selectedQuickFilterGroup of selectedQuickFilterGroups) {
      const selectedValues = selectedQuickFilterGroup.selectedValues;
      if (!selectedValues)
        continue;
      outer:for (let i = 0; i < selectedValues.length; i++) {
        const selectedButton = selectedValues[i];
        if (quickFilterButton.columnMetadata?.length !== selectedButton.columnMetadata?.length)
          continue;
        if (!quickFilterButton.columnMetadata?.length)
          continue;
        for (let j = 0; j < quickFilterButton.columnMetadata?.length; j++) {
          const buttonToAddMetaDataValue = quickFilterButton.columnMetadata[j];
          const selectedButtonMetaData = selectedButton.columnMetadata?.[j];
          if (buttonToAddMetaDataValue?.fieldId != selectedButtonMetaData?.fieldId)
            continue outer;
          if (j != quickFilterButton.columnMetadata?.length - 1)
            continue;
          selectedValues.push(quickFilterButton);
          return;
        }
      }
    }
    selectedQuickFilterGroups.push({selectedValues: [quickFilterButton]})
  }

  private unSelectQuickFilter(quickFilterButton: namaTypes.ListViewQuickFilterButtonVal) {
    const selectedQuickFilterGroups = this.presenter.selectedQuickFilterGroups.value;
    for (const selectedQuickFilterGroup of selectedQuickFilterGroups) {
      const selectedValues = selectedQuickFilterGroup.selectedValues;
      if (!selectedValues)
        continue;
      for (const selectedValue of selectedValues) {
        if (ObjectChecker.areEqual(selectedValue, quickFilterButton)) {
          selectedValues.splice(selectedValues.indexOf(quickFilterButton), 1);
          if (!selectedValues.length)
            selectedQuickFilterGroups.splice(selectedQuickFilterGroups.indexOf(selectedQuickFilterGroup), 1)
          return;
        }
      }
    }
  }

  isDropdown(group: ListViewQuickFilterGroupVal): boolean {
    return !!group.dropdown
  }

  getModelValueForDropdown(group: ListViewQuickFilterGroupVal): WritableComputedRef<ListViewQuickFilterButtonVal[]> {
    return computed({
      get: () => {
        const selectedButtons: ListViewQuickFilterButtonVal[] = [];
        for (const button of group.buttons ?? []) {
          if (this.isQuickFilterButtonSelected(button).value)
            selectedButtons.push(button)
        }
        return selectedButtons;
      },
      set: (newValues: ListViewQuickFilterButtonVal[]) => {
        this.toggleSelectedQuickFilterButtonForDropdown(group, newValues);
      }
    });
  }

  private static readonly quickFilterSerializer = new QuickFilterURLSerializer();

  static syncQuickFiltersToURL(presenter: ListPresenter) {
    const selected = presenter.selectedQuickFilterGroups.value;
    if (!selected?.length) {
      presenter.fetchPlace().quickFilters = undefined;
    } else {
      const encoded = this.quickFilterSerializer.toURL(selected);
      presenter.fetchPlace().quickFilters = encoded || undefined;
    }
    this.silentlyUpdateQuickFiltersInURL(presenter);
  }

  private static silentlyUpdateQuickFiltersInURL(presenter: ListPresenter) {
    const query = presenter.fetchPlace().fetchQuery() as Record<string, string | undefined>;
    if (ObjectChecker.areEqual(router.currentRoute.value.query, query))
      return;
    presenter.ignoreNextRouteChange = true;
    router.replace({
      query
    });
    presenter.ignoreNextRouteChange = false;
  }

  static deserializeFromURL(encodedQf: string | undefined): ListViewQuickFilterSelectedValues[] {
    if (!encodedQf)
      return [];
    const buttons = this.quickFilterSerializer.fromURL(encodedQf);
    if (!buttons.length)
      return [];
    const result: ListViewQuickFilterSelectedValues[] = [];
    for (const button of buttons)
      this.addButtonToGroups(button, result);
    return result;
  }

  static restoreQuickFiltersFromURL(presenter: ListPresenter) {
    const qf = presenter.fetchPlace().quickFilters;
    if (!qf)
      return;
    const groups = presenter.quickFilterGroup.value;
    if (!groups?.length)
      return;
    const serializedButtons = this.quickFilterSerializer.fromURL(qf);
    if (!serializedButtons.length)
      return;
    for (const serialized of serializedButtons) {
      for (const group of groups) {
        if (!group.buttons?.length)
          continue;
        for (const button of group.buttons) {
          if (this.quickFilterSerializer.matchesButton(serialized, button))
            this.addButtonToGroups(button, presenter.selectedQuickFilterGroups.value);
        }
      }
    }
  }

  private static addButtonToGroups(button: ListViewQuickFilterButtonVal, groups: ListViewQuickFilterSelectedValues[]) {
    for (const group of groups) {
      const selectedValues = group.selectedValues;
      if (!selectedValues?.length) continue;
      if (!button.columnMetadata?.length) continue;
      if (selectedValues[0].columnMetadata?.length !== button.columnMetadata.length) continue;
      let match = true;
      for (let j = 0; j < button.columnMetadata.length; j++) {
        if (button.columnMetadata[j].fieldId !== selectedValues[0].columnMetadata?.[j]?.fieldId) {
          match = false;
          break;
        }
      }
      if (match) {
        selectedValues.push(button);
        return;
      }
    }
    groups.push({selectedValues: [button]});
  }

}
